import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  ApiRequestError,
  createReleaseWave,
  executeReleaseWave,
  fetchCourses,
  fetchGroups,
  fetchQueueItems,
  fetchQueueStats,
  fetchReleaseWaves,
  rollbackReleaseWave,
  type CourseSummary,
  type GroupNode,
} from '../api/client.js';
import type { QueueStats, ReleaseWaveSummary, WorkItemSummary } from '../types/workflow.js';
import { isMachineActor, MachineActorBadge } from './MachineActorBadge.js';
import { readCourseFromLocation, resolveCourseSelection } from '../lib/courseSelection.js';
import { describeApiError } from '../lib/apiErrorMessage.js';

type WaveBanner = {
  tone: 'error' | 'success';
  message: string;
};

const EMPTY_COURSES: CourseSummary[] = [];

export function ReleaseWavesDashboard({ courses = EMPTY_COURSES }: { courses?: CourseSummary[] }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [waves, setWaves] = useState<ReleaseWaveSummary[]>([]);
  const [allItems, setAllItems] = useState<WorkItemSummary[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [availableCourses, setAvailableCourses] = useState<CourseSummary[]>(courses);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedWaveId, setExpandedWaveId] = useState<string | null>(null);
  const [banner, setBanner] = useState<WaveBanner | null>(null);
  const [acting, setActing] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [groupTitleIndex, setGroupTitleIndex] = useState<Map<string, string>>(new Map());

  // Every spelling, then the level last chosen in this tab, then the authoring default.
  // See `lib/courseSelection.ts`.
  const { courseKey } = resolveCourseSelection(
    availableCourses,
    readCourseFromLocation(location.pathname, searchParams),
  );

  useEffect(() => {
    if (courses.length > 0) {
      setAvailableCourses(courses);
      return;
    }
    let cancelled = false;
    void fetchCourses()
      .then((loaded) => { if (!cancelled) setAvailableCourses(loaded); })
      .catch(() => { if (!cancelled) setAvailableCourses([]); });
    return () => { cancelled = true; };
  }, [courses]);

  /**
   * A generation counter, because switching level makes a stale response reachable.
   *
   * `loadData` fires ten requests and has no cancellation. Change course while the first course's
   * requests are still in flight and the slower set lands last, overwriting the new course's waves
   * and items — under a header that by then names the new level. The counter is bumped before every
   * load and checked before every `setState`, so a response for a course that is no longer selected
   * is discarded rather than rendered.
   */
  const loadGeneration = useRef(0);

  useEffect(() => {
    if (!courseKey) return;
    // Clear the previous course's content BEFORE the new load, so nothing from the old level can be
    // on screen under the new level's name even for one frame.
    setWaves([]);
    setAllItems([]);
    setStats(null);
    setExpandedWaveId(null);
    setSelectedItemIds(new Set());
    setShowCreateDialog(false);
    setStatusFilter(null);
    void loadData();
    // CLEANUP INVALIDATES THE GENERATION, and that covers the case the counter alone does not: an
    // unmount with no second load behind it. Without this, a response in flight when the screen is
    // torn down is still "current" and runs its `setState` calls into a dead component — and a
    // `createReleaseWave` continuation would go further and start ten fresh reads after unmount.
    return () => { loadGeneration.current += 1; };
  }, [courseKey]);

  /**
   * SCOPED AGAIN ON THE CLIENT, and the drop is counted rather than swallowed.
   *
   * Every list below is fetched with `{ courseKey }` and the server scopes it (`queue.service.ts`
   * `listByStatusForCourse`) — that remains the primary guarantee. This is the backstop the sibling
   * screen already had and this one did not: `WorkQueueDashboard` re-filters its response and raises
   * a visible notice, while this screen derived its approved, pending and unassigned lists straight
   * from the raw response, so a foreign item reaching it would have been **displayed and tickable in
   * the create-wave dialog**. `ReleaseService.createWave` refuses a cross-course roster at the end,
   * so it could never have shipped — but the reviewer would have seen another exam level's work
   * offered under this level's heading, which is the thing he asked us to prevent:
   *
   * > "there are topics that are similar but not tested between levels and I wouldn't want to get my
   * > wires crossed upon review as the lines blur."
   *
   * **Counted and shown, never silent.** A guard that quietly discarded would make *not looking* the
   * cheapest way to pass it, and a server regression would then present as a perfectly correct
   * screen — the denominator-escape shape of the vacuous-check table (`L-1195`).
   */
  const courseScoped = useMemo(() => {
    if (!courseKey) return { items: allItems, foreignCount: 0 };
    const inCourse = allItems.filter((item) => item.courseKey === courseKey);
    return { items: inCourse, foreignCount: allItems.length - inCourse.length };
  }, [allItems, courseKey]);

  const itemIndex = useMemo(
    () => new Map(courseScoped.items.map((item) => [item.workItemId, item])),
    [courseScoped],
  );

  const courseLabel = useMemo(
    () => availableCourses.find((c) => c.id === courseKey)?.title ?? courseKey,
    [availableCourses, courseKey],
  );

  const allCourseWaves = useMemo(
    () => waves
      .filter((w) => w.courseKey === courseKey)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [courseKey, waves],
  );

  const courseWaves = useMemo(() => {
    if (!statusFilter) return allCourseWaves;
    return allCourseWaves.filter((w) => w.status === statusFilter);
  }, [allCourseWaves, statusFilter]);

  const approvedItems = useMemo(
    () => courseScoped.items.filter((item) => item.status === 'approved' || item.status === 'approved_with_edit'),
    [courseScoped],
  );

  const pendingItems = useMemo(
    () => courseScoped.items.filter((item) => item.status === 'pending'),
    [courseScoped],
  );

  const unassignedApproved = useMemo(() => {
    const waveItemIds = new Set(allCourseWaves.flatMap((w) => w.workItemIds));
    return approvedItems.filter((item) => !waveItemIds.has(item.workItemId));
  }, [approvedItems, allCourseWaves]);

  if (loading) {
    return <StatePanel message="Loading release waves..." tone="neutral" />;
  }

  if (error) {
    return <StatePanel message={error} tone="error" />;
  }

  const statCounts = stats?.countsByStatus ?? {};
  const totalItems = stats?.total ?? 0;
  const executedCount = (statCounts as Record<string, number>).executed ?? 0;
  const approvedCount =
    ((statCounts as Record<string, number>).approved ?? 0) +
    ((statCounts as Record<string, number>).approved_with_edit ?? 0);
  const pendingCount = (statCounts as Record<string, number>).pending ?? 0;

  return (
    <main className="flex-1 overflow-y-auto bg-background-light" data-testid="waves-dashboard">
      {banner ? (
        <div className="px-8 pt-4">
          <div className={banner.tone === 'error'
            ? 'rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800'
            : 'rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800'}>
            {banner.message}
          </div>
        </div>
      ) : null}

      {/* Stats Bar */}
      <div className="border-b border-primary/5 bg-white px-8 py-6">
        <div className="flex flex-wrap gap-4">
          <StatCard active={!statusFilter} label="Total Items" value={totalItems} tone="default" icon="inventory_2" onClick={() => setStatusFilter(null)} />
          <StatCard active={statusFilter === 'executed'} label="Executed" value={executedCount} tone="emerald" icon="check_circle" onClick={() => setStatusFilter(statusFilter === 'executed' ? null : 'executed')} />
          <StatCard active={statusFilter === 'draft'} label="Approved Ready" value={approvedCount} tone="blue" icon="verified" onClick={() => setStatusFilter(statusFilter === 'draft' ? null : 'draft')} />
          <StatCard active={statusFilter === 'pending'} label="In Review" value={pendingCount} tone="amber" icon="pending" onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')} />
        </div>
      </div>

      <div className="space-y-6 p-8">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Release Waves</h1>
            <p className="text-sm text-slate-500">{courseLabel}</p>
          </div>
          {unassignedApproved.length > 0 ? (
            <button
              className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-slate-800"
              onClick={() => {
                setSelectedItemIds(new Set(unassignedApproved.map((i) => i.workItemId)));
                setShowCreateDialog(true);
              }}
              type="button"
            >
              <span className="material-symbols-outlined text-[18px]">add</span>
              Create Wave ({unassignedApproved.length} ready)
            </button>
          ) : null}
        </div>

        {courseScoped.foreignCount > 0 ? (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
            data-testid="foreign-course-items-notice"
          >
            The server returned {courseScoped.foreignCount} item
            {courseScoped.foreignCount === 1 ? '' : 's'} belonging to another course, which should be
            impossible: this screen asked only about {courseLabel} ({courseKey}).
            {courseScoped.foreignCount === 1 ? ' It is' : ' They are'} not shown and cannot be put in
            a wave, and no wave you have already created is affected. Please report this before
            releasing anything further — course scoping is what keeps one exam level's work out of
            another's.
          </div>
        ) : null}

        {courseWaves.length === 0 && pendingItems.length === 0 && unassignedApproved.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
            No release waves yet. Approve items in the Work Queue to create your first wave.
          </div>
        ) : null}

        {courseWaves.map((wave) => (
          <WaveLane
            acting={acting}
            expanded={expandedWaveId === wave.waveId}
            groupTitleIndex={groupTitleIndex}
            itemIndex={itemIndex}
            key={wave.waveId}
            onExecute={() => { void handleExecute(wave.waveId); }}
            onRollback={() => { void handleRollback(wave.waveId); }}
            onToggle={() => setExpandedWaveId((c) => (c === wave.waveId ? null : wave.waveId))}
            wave={wave}
          />
        ))}

        {(!statusFilter || statusFilter === 'pending') && pendingItems.length > 0 ? (
          <section className="overflow-hidden rounded-xl border-l-4 border-slate-300 bg-white shadow-sm" data-testid="pending-items-section">
            <div className="flex items-center justify-between p-5">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-primary">Pending Review</h3>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-600">
                    {pendingItems.length} items
                  </span>
                </div>
                <p className="text-sm text-slate-500">Items awaiting approval in the Work Queue before they can be added to a wave</p>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto border-t border-slate-100 bg-slate-50/50">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-6 py-3">Entity Name</th>
                    <th className="px-6 py-3">Operation</th>
                    <th className="px-6 py-3">Priority</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Cluster</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pendingItems.map((item) => (
                    <tr className="transition-colors hover:bg-slate-100/50" key={item.workItemId} data-testid="pending-item-row">
                      <td className="px-6 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-slate-400">{targetTypeIcon(item.targetType)}</span>
                          {itemTitle(item, groupTitleIndex)}
                        </div>
                      </td>
                      <td className="px-6 py-3"><span className={opBadgeClass(item.op)}>{opLabel(item.op)}</span></td>
                      <td className="px-6 py-3 text-xs font-bold">{item.priority}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-[18px] text-amber-500">pending</span>
                          Pending
                        </div>
                      </td>
                      <td className="px-6 py-3 text-right text-xs text-slate-500">{item.clusterKey}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </div>

      {showCreateDialog ? (
        <CreateWaveDialog
          courseKey={courseKey}
          groupTitleIndex={groupTitleIndex}
          items={unassignedApproved}
          onClose={() => setShowCreateDialog(false)}
          onCreate={handleCreateWave}
          selectedIds={selectedItemIds}
          setSelectedIds={setSelectedItemIds}
        />
      ) : null}
    </main>
  );

  async function loadData() {
    const generation = ++loadGeneration.current;
    const isCurrent = () => loadGeneration.current === generation;
    setLoading(true);
    setError('');
    try {
      const [
        allWaves,
        queueStats,
        groups,
        pendingQueue,
        approvedQueue,
        approvedEditQueue,
        executingQueue,
        executedQueue,
        rolledBackQueue,
        rollbackFailedQueue,
        cancelledQueue,
      ] = await Promise.all([
        fetchReleaseWaves(),
        fetchQueueStats(courseKey),
        fetchGroups(courseKey).catch(() => [] as GroupNode[]),
        fetchQueueItems({ courseKey, status: 'pending' }),
        fetchQueueItems({ courseKey, status: 'approved' }),
        fetchQueueItems({ courseKey, status: 'approved_with_edit' }),
        fetchQueueItems({ courseKey, status: 'executing' }),
        fetchQueueItems({ courseKey, status: 'executed' }),
        fetchQueueItems({ courseKey, status: 'rolled_back' }),
        fetchQueueItems({ courseKey, status: 'rollback_failed' }),
        fetchQueueItems({ courseKey, status: 'cancelled' }),
      ]);
      // A response for a course that is no longer selected is DISCARDED, not rendered. Without this
      // a slow Level I load could land after a fast Level III one and put Level I's waves under a
      // header naming Level III.
      if (!isCurrent()) return;
      setWaves(allWaves);
      setAllItems(indexWorkItemsById([
        ...pendingQueue.items,
        ...approvedQueue.items,
        ...approvedEditQueue.items,
        ...executingQueue.items,
        ...executedQueue.items,
        ...rolledBackQueue.items,
        ...rollbackFailedQueue.items,
        ...cancelledQueue.items,
      ]));
      setStats(queueStats);
      setGroupTitleIndex(buildGroupTitleIndex(groups));
    } catch (reason) {
      if (!isCurrent()) return;
      setError(reason instanceof Error ? reason.message : 'Unable to load release data');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }

  async function handleCreateWave(workItemIds: string[]) {
    if (!courseKey || workItemIds.length === 0) return;
    setActing(true);
    try {
      await createReleaseWave(workItemIds, courseKey);
      setBanner({ tone: 'success', message: `Created wave with ${workItemIds.length} items.` });
      setShowCreateDialog(false);
      await loadData();
    } catch (reason) {
      setBanner({
        tone: 'error',
        message: reason instanceof Error ? reason.message : 'Failed to create wave.',
      });
    } finally {
      setActing(false);
    }
  }

  async function handleExecute(waveId: string) {
    if (!confirm('Execute this wave? This will apply all approved changes to the course structure.')) return;
    setActing(true);
    setBanner(null);
    try {
      const result = await executeReleaseWave(waveId);
      // A SKIPPED ITEM IS NOT A SUCCESS. The executor acts only on approved members, so a skip
      // means a member left that state after the wave was composed — and for an atomic group that
      // is the half-apply the whole mechanism exists to prevent. It used to render as a green
      // banner with a bare count and no ids, which is unactionable and untrue.
      setBanner(
        result.skippedItemIds.length > 0
          ? {
            tone: 'error',
            message:
              `Wave executed: ${result.executedItemIds.length} items applied, but `
              + `${result.skippedItemIds.length} were NOT applied — ${result.skippedItemIds.join(', ')}. `
              + 'Check each one before composing another wave; if any belongs to an all-or-nothing group, '
              + 'part of that group may now be live.',
          }
          : {
            tone: 'success',
            message: `Wave executed. ${result.executedItemIds.length} items applied.`,
          },
      );
      await loadData();
    } catch (reason) {
      // A 409 IS NOT EVIDENCE THAT THE WAVE RAN. The server refuses a draft wave on policy grounds
      // with 409 and a full explanation — `reason: 'edits_envelope_invalid'`, the work item and the
      // field — and leaves the wave in DRAFT. This line used to replace every 409 with "already
      // executed or is in progress", telling the reviewer his wave had run when it had not, about a
      // wave still in front of him with an Execute button on it. The server's own words come first
      // now; the old sentence survives only as a hedge for a 409 that explains nothing, and is
      // worded as the guess it always was.
      const msg = describeApiError(
        reason,
        reason instanceof ApiRequestError && reason.status === 409
          ? 'The server refused to execute this wave and sent no explanation. Check the wave status '
            + 'before retrying — it may already be executed or in progress, or it may have been '
            + 'refused while still in draft.'
          : 'Execution failed.',
      );
      setBanner({ tone: 'error', message: msg });
    } finally {
      setActing(false);
    }
  }

  async function handleRollback(waveId: string) {
    if (!confirm('Rollback this wave? This will revert all executed changes.')) return;
    setActing(true);
    setBanner(null);
    try {
      const result = await rollbackReleaseWave(waveId);
      setBanner({
        tone: 'success',
        message: `Rolled back ${result.rolledBackItemIds.length} items.`,
      });
      await loadData();
    } catch (reason) {
      setBanner({
        tone: 'error',
        message: reason instanceof Error ? reason.message : 'Rollback failed.',
      });
    } finally {
      setActing(false);
    }
  }
}

/* ---------- Wave Lane ---------- */

function WaveLane({
  wave,
  expanded,
  acting,
  itemIndex,
  groupTitleIndex,
  onToggle,
  onExecute,
  onRollback,
}: {
  wave: ReleaseWaveSummary;
  expanded: boolean;
  acting: boolean;
  itemIndex: Map<string, WorkItemSummary>;
  groupTitleIndex: Map<string, string>;
  onToggle: () => void;
  onExecute: () => void;
  onRollback: () => void;
}) {
  const status = wave.status as string;
  const isExecuted = status === 'executed';
  const isDraft = status === 'draft';
  const isExecuting = status === 'executing';
  const isRolledBack = status === 'rolled_back' || status === 'partially_rolled_back';
  const itemCount = wave.workItemIds.length;

  const borderColor = isExecuted ? 'border-emerald-500'
    : isDraft ? 'border-amber-500'
    : isExecuting ? 'border-blue-500'
    : isRolledBack ? 'border-rose-400'
    : 'border-slate-300';

  const statusBadge = isExecuted
    ? { bg: 'bg-emerald-100 text-emerald-700', label: 'Executed' }
    : isDraft
    ? { bg: 'bg-amber-100 text-amber-700', label: 'Draft' }
    : isExecuting
    ? { bg: 'bg-blue-100 text-blue-700', label: 'Executing' }
    : isRolledBack
    ? { bg: 'bg-rose-100 text-rose-700', label: 'Rolled Back' }
    : { bg: 'bg-slate-100 text-slate-600', label: status };

  return (
    <section className={`overflow-hidden rounded-xl border-l-4 bg-white shadow-sm ${borderColor}`} data-testid="wave-lane">
      <div
        className="flex cursor-pointer items-center justify-between p-5 transition-colors hover:bg-slate-50/50"
        onClick={onToggle}
        role="button"
        aria-expanded={expanded}
        aria-label={expanded ? `Collapse ${wave.waveId}` : `Expand ${wave.waveId}`}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-primary">{wave.waveId}</h3>
            <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${statusBadge.bg}`} data-testid="wave-status">
              {statusBadge.label}
            </span>
          </div>
          <p className="text-sm text-slate-500">
            {itemCount} items
            {wave.createdBy ? ` · Created by ${wave.createdBy}` : ''}
            {wave.executedAt ? ` · Executed ${new Date(wave.executedAt).toLocaleDateString()}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-4">
          {isDraft ? (
            <button
              className="flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={acting}
              onClick={(e) => { e.stopPropagation(); onExecute(); }}
              type="button"
            >
              <span className="material-symbols-outlined text-[18px]">publish</span>
              Execute Wave
            </button>
          ) : null}
          {isExecuted ? (
            <button
              className="flex items-center gap-2 rounded-lg border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={acting}
              onClick={(e) => { e.stopPropagation(); onRollback(); }}
              type="button"
            >
              <span className="material-symbols-outlined text-[18px]">history</span>
              Rollback Wave
            </button>
          ) : null}
          <span className={`material-symbols-outlined text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}>expand_more</span>
        </div>
      </div>

      {expanded ? (
        <div className="max-h-64 overflow-y-auto border-t border-slate-100 bg-slate-50/50" data-testid="wave-detail-table">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-3">Entity Name</th>
                <th className="px-6 py-3">Operation</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Final Approver</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {wave.workItemIds.map((id) => {
                const item = itemIndex.get(id);
                return (
                  <tr className="transition-colors hover:bg-slate-100/50" key={id} data-testid="wave-item-row">
                    <td className="px-6 py-4 font-medium">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-slate-400">{targetTypeIcon(item?.targetType)}</span>
                        {item ? itemTitle(item, groupTitleIndex) : id}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={opBadgeClass(item?.op)}>{opLabel(item?.op)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`material-symbols-outlined text-[18px] ${statusIconClass(item?.status)}`}>
                          {statusIcon(item?.status)}
                        </span>
                        {statusLabel(item?.status)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-xs text-slate-500">
                      {/* The operator stays named; the badge says the approval was not an
                          individual human read. Its tooltip carries decision + batch. */}
                      {isMachineActor({ actorType: item?.reviewerActorType }) ? (
                        <span className="mr-1.5 inline-block align-middle font-sans text-[10px]">
                          <MachineActorBadge authority={item?.reviewerAuthority} testId="approver-machine-actor" />
                        </span>
                      ) : null}
                      {item?.reviewerId?.toUpperCase() ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

/* ---------- Create Wave Dialog ---------- */

function CreateWaveDialog({
  courseKey,
  groupTitleIndex,
  items,
  selectedIds,
  setSelectedIds,
  onClose,
  onCreate,
}: {
  courseKey: string;
  groupTitleIndex: Map<string, string>;
  items: WorkItemSummary[];
  selectedIds: Set<string>;
  setSelectedIds: (ids: Set<string>) => void;
  onClose: () => void;
  onCreate: (ids: string[]) => void;
}) {
  const allSelected = selectedIds.size === items.length;

  // Composition-time closure: `createWave` runs `assertAtomicGroupsClosed` and refuses a wave that
  // carries part of an all-or-nothing group. The dialog therefore selects by GROUP, not by row —
  // and where a group has a member this dialog cannot offer (not yet approved, or already in
  // another wave) it says so up front instead of letting the server refuse after the click.
  const byId = new Map(items.map((item) => [item.workItemId, item]));

  function groupOf(item: WorkItemSummary): string[] {
    return item.atomicGroup?.memberWorkItemIds ?? [item.workItemId];
  }

  function toggle(item: WorkItemSummary) {
    const next = new Set(selectedIds);
    const members = groupOf(item).filter((id) => byId.has(id));
    if (next.has(item.workItemId)) {
      for (const id of members) next.delete(id);
    } else {
      for (const id of members) next.add(id);
    }
    setSelectedIds(next);
  }

  // Members of a selected group that this dialog cannot offer. Named, because "the wave was
  // refused" is not something a reviewer can act on and a work item id is.
  const unavailableMembers = [...new Set(
    [...selectedIds]
      .flatMap((id) => groupOf(byId.get(id) ?? ({ workItemId: id } as WorkItemSummary)))
      .filter((memberId) => !byId.has(memberId)),
  )].sort();

  // A member that is NOT a work item at all — a repo B key-lane spec, which ships by another
  // mechanism. `assertAtomicGroupsClosed` refuses such a group outright (`external_member_unobservable`)
  // and `createWave` has no acknowledgement path for it, so a wave containing one is refused with
  // every work item present and correct. Without this the dialog looked entirely healthy and the
  // refusal arrived after the click.
  const externalBlockers = [...new Set(
    [...selectedIds].flatMap((id) => (byId.get(id)?.atomicGroup?.externalMembers ?? []).map((entry) => entry.ref)),
  )].sort();

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/35 p-4" data-testid="create-wave-dialog">
      <div className="grid w-full max-w-2xl gap-4 rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-bold text-primary">Create Release Wave</h2>
        <p className="text-sm text-slate-500">
          Select approved items to include in this wave for <strong>{courseKey}</strong>.
        </p>

        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">
                  <input
                    checked={allSelected}
                    onChange={() => {
                      setSelectedIds(allSelected ? new Set() : new Set(items.map((i) => i.workItemId)));
                    }}
                    type="checkbox"
                  />
                </th>
                <th className="px-4 py-2">Entity Name</th>
                <th className="px-4 py-2">Op</th>
                <th className="px-4 py-2">Priority</th>
                <th className="px-4 py-2">Cluster</th>
                <th className="px-4 py-2">Group</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => (
                <tr className="hover:bg-slate-50" key={item.workItemId}>
                  <td className="px-4 py-2">
                    <input
                      checked={selectedIds.has(item.workItemId)}
                      onChange={() => toggle(item)}
                      type="checkbox"
                    />
                  </td>
                  <td className="px-4 py-2 font-medium text-slate-800">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-sm text-slate-400">{targetTypeIcon(item.targetType)}</span>
                      {itemTitle(item, groupTitleIndex)}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={opBadgeClass(item.op)}>{opLabel(item.op)}</span>
                  </td>
                  <td className="px-4 py-2 text-xs">{item.priority}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{item.clusterKey}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs">
                    {item.atomicGroup ? (
                      <span
                        className="inline-block whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-semibold text-amber-700"
                        data-testid="wave-group-cell-grouped"
                        title="All-or-nothing: every member of this group ships together, or the wave is refused."
                      >
                        {item.atomicGroup.groupId} · {item.atomicGroup.memberWorkItemIds.length}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {unavailableMembers.length > 0 ? (
          <p
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800"
            data-testid="wave-group-incomplete"
          >
            This selection includes an all-or-nothing group whose other member(s) are not available
            here: {unavailableMembers.join(', ')}. A wave carrying part of a group is refused, so
            approve or release those first — or deselect the group.
          </p>
        ) : null}

        {externalBlockers.length > 0 ? (
          <p
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800"
            data-testid="wave-group-external"
          >
            This selection includes an all-or-nothing group that also contains a change shipping
            outside this tool ({externalBlockers.join(', ')}). A wave cannot be shown complete for
            such a group, so it will be refused — deselect it.
          </p>
        ) : null}

        <div className="flex justify-end gap-3">
          <button
            className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={selectedIds.size === 0 || unavailableMembers.length > 0 || externalBlockers.length > 0}
            onClick={() => onCreate([...selectedIds])}
            type="button"
          >
            Create Wave ({selectedIds.size} items)
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Stat Card ---------- */

function StatCard({
  label,
  value,
  tone,
  icon,
  active = false,
  onClick,
}: {
  label: string;
  value: number;
  tone: 'default' | 'emerald' | 'blue' | 'amber' | 'rose';
  icon: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const toneClasses: Record<string, string> = {
    default: 'bg-slate-50 border-slate-100 text-primary',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    blue: 'bg-blue-50 border-blue-100 text-blue-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    rose: 'bg-rose-50 border-rose-100 text-rose-700',
  };

  const iconTone: Record<string, string> = {
    default: 'text-slate-400',
    emerald: 'text-emerald-500',
    blue: 'text-blue-500',
    amber: 'text-amber-500',
    rose: 'text-rose-500',
  };

  const ring = active ? 'ring-2 ring-primary/30' : '';

  return (
    <button
      className={`min-w-[180px] flex-1 cursor-pointer rounded-xl border p-4 text-left transition-all hover:shadow-md ${toneClasses[tone]} ${ring}`}
      data-testid="stat-card"
      onClick={onClick}
      type="button"
    >
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold">{value}</span>
        <span className={`material-symbols-outlined text-[18px] ${iconTone[tone]}`}>{icon}</span>
      </div>
    </button>
  );
}

/* ---------- Helpers ---------- */

function itemTitle(item: WorkItemSummary, groupTitles?: Map<string, string>): string {
  const titlePatch = item.suggestedPatch.title;
  if (typeof titlePatch === 'string') return titlePatch;
  if (titlePatch && typeof titlePatch === 'object') {
    const after = (titlePatch as Record<string, unknown>).after;
    const before = (titlePatch as Record<string, unknown>).before;
    if (typeof after === 'string' && after.trim()) return after;
    if (typeof before === 'string' && before.trim()) return before;
  }
  if (typeof item.suggestedPatch.name === 'string') return item.suggestedPatch.name;
  // For archive/delete ops with empty patches, look up the group's current title
  if (item.targetId && groupTitles) {
    const resolved = groupTitles.get(item.targetId) ?? groupTitles.get(`group:${item.targetId}`);
    if (resolved) return resolved;
  }
  return item.targetId ?? item.workItemId;
}

function buildGroupTitleIndex(groups: GroupNode[]): Map<string, string> {
  const index = new Map<string, string>();
  const visit = (nodes: GroupNode[]) => {
    for (const node of nodes) {
      index.set(String(node.id), node.title);
      index.set(`group:${node.id}`, node.title);
      visit(node.children);
    }
  };
  visit(groups);
  return index;
}

function opLabel(op?: string): string {
  switch (op) {
    case 'rename': return 'Rename';
    case 'create': return 'Add';
    case 'archive': return 'Delete';
    case 'move': return 'Move';
    case 'reorder': return 'Reorder';
    case 'rewrite': return 'Update';
    case 'augment_practice': return 'Practice';
    case 'fix': return 'Fix';
    default: return op ?? '—';
  }
}

function opBadgeClass(op?: string): string {
  switch (op) {
    case 'create':
      return 'rounded border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-600';
    case 'archive':
      return 'rounded border border-rose-100 bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-500';
    case 'rename':
    case 'rewrite':
      return 'rounded border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-600';
    default:
      return 'rounded border border-slate-100 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600';
  }
}

function statusIcon(status?: string): string {
  switch (status) {
    case 'approved':
    case 'approved_with_edit':
    case 'executed':
      return 'check_circle';
    case 'executing':
      return 'hourglass_top';
    case 'pending':
      return 'pending';
    case 'cancelled':
      return 'cancel';
    case 'rolled_back':
      return 'undo';
    case 'rollback_failed':
      return 'error';
    default:
      return 'radio_button_unchecked';
  }
}

function statusIconClass(status?: string): string {
  switch (status) {
    case 'approved':
    case 'approved_with_edit':
    case 'executed':
      return 'text-emerald-500';
    case 'executing':
      return 'text-blue-500';
    case 'pending':
      return 'text-amber-500';
    case 'cancelled':
    case 'rolled_back':
      return 'text-rose-400';
    case 'rollback_failed':
      return 'text-rose-600';
    default:
      return 'text-slate-400';
  }
}

function targetTypeIcon(targetType?: string): string {
  switch (targetType) {
    case 'group': return 'folder';
    case 'entity': return 'menu_book';
    case 'task': return 'quiz';
    default: return 'label';
  }
}

function statusLabel(status?: string): string {
  switch (status) {
    case 'approved': return 'Approved';
    case 'approved_with_edit': return 'Approved (edited)';
    case 'executing': return 'Executing';
    case 'executed': return 'Executed';
    case 'pending': return 'Pending';
    case 'cancelled': return 'Cancelled';
    case 'rolled_back': return 'Rolled back';
    case 'rollback_failed': return 'Rollback failed';
    default: return status ?? '—';
  }
}

function indexWorkItemsById(items: WorkItemSummary[]) {
  const byId = new Map<string, WorkItemSummary>();
  items.forEach((item) => {
    byId.set(item.workItemId, item);
  });
  return [...byId.values()];
}

function StatePanel({ message, tone }: { message: string; tone: 'error' | 'neutral' }) {
  return (
    <div className={tone === 'error'
      ? 'grid min-h-[18rem] place-items-center rounded-[1.5rem] border border-rose-200 bg-rose-50 px-6 text-center text-sm text-rose-800 shadow-sm'
      : 'grid min-h-[18rem] place-items-center rounded-[1.5rem] border border-slate-200 bg-white px-6 text-center text-sm text-slate-500 shadow-sm'}>
      <span className={tone === 'neutral' ? 'animate-pulse' : ''}>{message}</span>
    </div>
  );
}
